import React, { useState, useEffect } from 'react';
import { safeUrl } from '../lib/safeUrl';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateInTZ } from '../lib/localDate';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Building2, MapPin, DollarSign, ExternalLink, 
  Bookmark, BookmarkCheck, X, Globe, Calendar,
  Clock, ArrowLeft, Lock, Share2, FileText
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

const statusColors = {
  open: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  closed: 'bg-gray-500/10 text-gray-500 border-gray-500/30',
  awarded: 'bg-blue-500/10 text-blue-500 border-blue-500/30'
};

export default function SharedTenderPage() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const isRTL = language === 'ar';
  
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    fetchSharedTender();
  }, [shareToken, token]);

  const fetchSharedTender = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/shared/tender/${shareToken}`,
        { headers }
      );
      setTender(response.data.tender);
      setIsSaved(response.data.tender.is_saved);
      setIsAuthenticated(response.data.is_authenticated);
    } catch (err) {
      console.error('Error fetching shared tender:', err);
      setError(err.response?.data?.detail || 'Failed to load tender');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error(language === 'ar' ? 'يجب تسجيل الدخول لحفظ المناقصة' : 'Please log in to save this tender');
      navigate('/login');
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${BACKEND_URL}/api/admin/shared/save`,
        { item_type: 'tender', item_id: tender.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsSaved(true);
      toast.success(language === 'ar' ? 'تم حفظ المناقصة بنجاح' : 'Tender saved successfully');
    } catch (err) {
      console.error('Error saving tender:', err);
      toast.error(err.response?.data?.detail || 'Failed to save tender');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsave = async () => {
    try {
      setSaving(true);
      await axios.delete(
        `${BACKEND_URL}/api/admin/shared/unsave`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          data: { item_type: 'tender', item_id: tender.id }
        }
      );
      setIsSaved(false);
      toast.success(language === 'ar' ? 'تمت إزالة المناقصة من المحفوظات' : 'Tender removed from saved items');
    } catch (err) {
      console.error('Error removing saved tender:', err);
      toast.error(err.response?.data?.detail || 'Failed to remove tender');
    } finally {
      setSaving(false);
    }
  };

  const handleDisregard = () => {
    navigate('/dashboard/tenders');
  };

  const formatValue = (value, currency) => {
    if (!value) return null;
    const curr = currency || 'USD';
    if (value >= 1e9) return `${curr} ${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `${curr} ${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `${curr} ${(value / 1e3).toFixed(0)}K`;
    return `${curr} ${value.toLocaleString()}`;
  };

  const formatDate = (dateStr, tz) => {
    if (!dateStr) return null;
    // WO-12: format in the tender's own country timezone (from the API), not
    // the reader's browser zone, so a Gulf deadline shows the correct day.
    return formatDateInTZ(dateStr, tz, language);
  };

  const getTitle = () => {
    if (!tender?.title) return '';
    // Always show English first, fallback to Arabic
    return tender.title.en || tender.title.ar || '';
  };

  const getAuthority = () => {
    if (!tender?.authority) return '';
    // Always show English first, fallback to Arabic
    return tender.authority.en || tender.authority.ar || '';
  };

  const getLocation = () => {
    if (!tender?.location) return '';
    // Always show English first, fallback to Arabic
    return tender.location.en || tender.location.ar || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-32 mb-6" />
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-8 w-3/4 mb-4" />
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-4 w-1/3 mb-4" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {language === 'ar' ? 'المناقصة غير متاحة' : 'Tender Not Available'}
            </h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard/tenders')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'العودة للمناقصات' : 'Back to Tenders'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <Badge variant="outline" className="text-purple-500 border-purple-500/30">
            <Share2 className="w-3 h-3 mr-1" />
            {language === 'ar' ? 'مناقصة مشتركة' : 'Shared Tender'}
          </Badge>
          <Badge variant="outline" className="text-amber-500 border-amber-500/30">
            <Lock className="w-3 h-3 mr-1" />
            {language === 'ar' ? 'خاص' : 'Private'}
          </Badge>
        </div>

        {/* Tender Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <CardTitle className="text-2xl mb-2">{getTitle()}</CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-3">
                  <span className="flex items-center gap-1">
                    {countryFlags[tender.country]}
                    {countryNames[language][tender.country] || tender.country}
                  </span>
                  {getAuthority() && (
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {getAuthority()}
                    </span>
                  )}
                  {tender.reference_no && (
                    <span className="flex items-center gap-1 text-xs">
                      <FileText className="w-3 h-3" />
                      {tender.reference_no}
                    </span>
                  )}
                </CardDescription>
              </div>
              <Badge className={statusColors[tender.status] || statusColors.open}>
                {tender.status?.charAt(0).toUpperCase() + tender.status?.slice(1)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Value */}
            {tender.value && (
              <div className="flex items-center gap-2 text-lg font-medium text-emerald-600">
                <DollarSign className="w-5 h-5" />
                {formatValue(tender.value, tender.currency)}
              </div>
            )}

            {/* Dates */}
            <div className="flex flex-wrap gap-4 text-sm">
              {tender.published_at && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>{language === 'ar' ? 'تاريخ النشر:' : 'Published:'}</span>
                  <span>{formatDate(tender.published_at, tender.timezone)}</span>
                </div>
              )}
              {tender.deadline && (
                <div className="flex items-center gap-2 text-amber-600">
                  <Clock className="w-4 h-4" />
                  <span>{language === 'ar' ? 'آخر موعد:' : 'Deadline:'}</span>
                  <span className="font-medium">{formatDate(tender.deadline, tender.timezone)}</span>
                </div>
              )}
            </div>

            {/* Location */}
            {getLocation() && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                {getLocation()}
              </div>
            )}

            {/* Description */}
            {tender.description && (
              <div className="prose prose-sm max-w-none">
                <p className="text-muted-foreground">{tender.description}</p>
              </div>
            )}

            {/* Source URL */}
            {safeUrl(tender.source_url) && !tender.source_url.startsWith('manual://') && (
              <Button variant="outline" size="sm" asChild>
                <a href={safeUrl(tender.source_url)} target="_blank" rel="noopener noreferrer">
                  <Globe className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'عرض في البوابة الرسمية' : 'View on Official Portal'}
                  <ExternalLink className="w-3 h-3 ml-2" />
                </a>
              </Button>
            )}

            {/* Created By */}
            {tender.created_by_name && (
              <p className="text-sm text-muted-foreground">
                {language === 'ar' ? 'تم الإنشاء بواسطة' : 'Created by'}: {tender.created_by_name}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <p className="text-center text-muted-foreground mb-2 sm:mb-0">
                {language === 'ar' 
                  ? 'هل تريد حفظ هذه المناقصة في قائمتك؟' 
                  : 'Would you like to save this tender to your list?'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mt-4">
              {isSaved ? (
                <Button 
                  onClick={handleUnsave} 
                  variant="outline"
                  disabled={saving}
                  className="min-w-[160px]"
                >
                  <BookmarkCheck className="w-4 h-4 mr-2 text-emerald-500" />
                  {saving 
                    ? (language === 'ar' ? 'جاري الإزالة...' : 'Removing...') 
                    : (language === 'ar' ? 'محفوظ' : 'Saved')}
                </Button>
              ) : (
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="min-w-[160px] bg-emerald-600 hover:bg-emerald-700"
                >
                  <Bookmark className="w-4 h-4 mr-2" />
                  {saving 
                    ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') 
                    : (language === 'ar' ? 'حفظ في قائمتي' : 'Save to My Feed')}
                </Button>
              )}
              <Button 
                onClick={handleDisregard} 
                variant="outline"
                className="min-w-[160px]"
              >
                <X className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'تجاهل' : 'Disregard'}
              </Button>
            </div>
            {!isAuthenticated && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                <Button variant="link" onClick={() => navigate('/login')} className="p-0 h-auto">
                  {language === 'ar' ? 'سجل الدخول' : 'Log in'}
                </Button>
                {' '}
                {language === 'ar' ? 'لحفظ هذه المناقصة' : 'to save this tender'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
